import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, UserRound, UserRoundPlus, X } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Page } from '@/lib/paging'
import { formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, Modal } from '@/components/ui/primitives'
import { Permission } from '@/lib/permissions'
import { useSession } from '@/stores/session'
import type { AttachedCustomer } from '@/stores/cart'
import { t } from '@/i18n'

interface CustomerRow extends AttachedCustomer {
  phone: string | null
  isActive: boolean
  isOverLimit: boolean
}

/**
 * Attaches an account to the sale in progress.
 *
 * Kept to a search box rather than a list: a shop with a few hundred accounts cannot be scrolled
 * with a queue waiting, and the cashier almost always knows the name or the phone number.
 */
export function CustomerPicker({
  open,
  onClose,
  onPick,
}: {
  open: boolean
  onClose: () => void
  onPick: (customer: AttachedCustomer) => void
}) {
  const [search, setSearch] = useState('')
  const [isAdding, setAdding] = useState(false)

  const canAdd = useSession((state) => state.can)(Permission.ManageCustomers)

  const customers = useQuery({
    queryKey: ['customers', search],
    queryFn: () =>
      apiClient
        .get<Page<CustomerRow>>(`/api/customers?search=${encodeURIComponent(search)}`)
        .then((page) => page.items),
    enabled: open,
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Charge to an account')}
      description={t('Pick the customer this sale belongs to.')}
      footer={
        <>
          {/* The account being asked for is very often one that does not exist yet — a regular
              buying on credit for the first time, with a queue behind them. Sending the cashier
              to the customers screen to create it loses the cart. */}
          {canAdd && (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              <UserRoundPlus className="h-4 w-4" />
              {t('New customer')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
        </>
      }
    >
      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('Search by name or phone')}
        leading={<Search className="h-4 w-4" />}
        wrapperClassName="mb-3"
        autoComplete="off"
        autoFocus
      />

      {customers.data?.length === 0 ? (
        <p className="py-6 text-center text-sm text-[var(--text-secondary)]">
          {search ? 'Nobody matched that.' : 'No accounts yet.'}
        </p>
      ) : (
        <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
          {customers.data?.map((customer) => {
            // An account with nothing left to draw on can still be attached — the sale is then
            // simply paid by other means and recorded against them, which is how a regular
            // settles up and buys again in one visit.
            const isBlocked = !customer.isActive || customer.availableCreditUsd <= 0

            return (
              <li key={customer.id}>
                <button
                  type="button"
                  onClick={() => onPick(customer)}
                  // Named explicitly: the visible text is split across several elements, so
                  // without this the row announces as an unlabelled button.
                  aria-label={`${customer.name}, owes ${formatUsd(customer.balanceUsd)}, ${formatUsd(Math.max(0, customer.availableCreditUsd))} can be charged`}
                  className="surface-raised flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:border-[var(--accent)]"
                >
                  <UserRound className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" />

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-medium">{customer.name}</span>
                      {!customer.isActive && <Badge tone="neutral">{t('Closed')}</Badge>}
                      {customer.isOverLimit && <Badge tone="danger">{t('Over limit')}</Badge>}
                    </div>
                    <p className="tabular truncate text-xs text-[var(--text-secondary)]">
                      {customer.phone ?? t('No phone')} · {t('owes')}{' '}
                      <bdi className="tabular">{formatUsd(customer.balanceUsd)}</bdi>
                    </p>
                  </div>

                  <div className="shrink-0 text-right">
                    <p
                      className={`tabular text-sm font-semibold ${
                        isBlocked ? 'text-[var(--text-secondary)]' : 'text-success-500'
                      }`}
                    >
                      {formatUsd(Math.max(0, customer.availableCreditUsd))}
                    </p>
                    <p className="text-xs text-[var(--text-secondary)]">{t('can charge')}</p>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <NewCustomerDialog
        open={isAdding}
        initialName={search}
        onClose={() => setAdding(false)}
        onCreated={(customer) => {
          setAdding(false)
          onPick(customer)
        }}
      />
    </Modal>
  )
}

/**
 * Opens an account without leaving the sale.
 *
 * Only the name is asked for. A phone number and a credit limit matter later, and every extra
 * field here is another thing to key with a customer waiting — the limit starts at nothing, which
 * means the account exists and can be settled against but cannot yet be charged. Raising it is a
 * decision for whoever manages credit, taken on the customers screen rather than at the till.
 */
function NewCustomerDialog({
  open,
  initialName,
  onClose,
  onCreated,
}: {
  open: boolean
  initialName: string
  onClose: () => void
  onCreated: (customer: AttachedCustomer) => void
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(initialName)
  const [phone, setPhone] = useState('')
  const [creditLimit, setCreditLimit] = useState('0')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      // Whatever the cashier had already typed looking for them is almost always the name.
      setName(initialName)
      setPhone('')
      setCreditLimit('0')
      setError(null)
    }
  }, [open, initialName])

  const create = useMutation({
    mutationFn: () =>
      apiClient.post<{ id: number; name: string }>('/api/customers', {
        name: name.trim(),
        phone: phone.trim() || null,
        email: null,
        address: null,
        taxNumber: null,
        creditLimitUsd: Number(creditLimit) || 0,
        isActive: true,
        notes: null,
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['customers'] })
      onCreated({
        id: created.id,
        name: created.name,
        balanceUsd: 0,
        creditLimitUsd: Number(creditLimit) || 0,
        availableCreditUsd: Number(creditLimit) || 0,
      })
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? (caught.detail ?? caught.message)
          : 'Could not add that customer.',
      ),
  })

  const trimmed = name.trim()

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('New customer')}
      description={t('They are attached to this sale as soon as they are saved.')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={trimmed.length === 0 || create.isPending}
          >
            {create.isPending ? 'Saving…' : 'Save and attach'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label={t('Name')}
          value={name}
          autoFocus
          onChange={(event) => setName(event.target.value)}
          placeholder={t('Who the account is for')}
        />
        <Input
          label={t('Phone')}
          value={phone}
          inputMode="tel"
          onChange={(event) => setPhone(event.target.value)}
          hint={t('Optional, but it is how most accounts get found again')}
        />
        <Input
          label={t('Credit limit')}
          value={creditLimit}
          inputMode="decimal"
          onChange={(event) => setCreditLimit(event.target.value)}
          hint={t('Nothing can be charged on account until this is above zero')}
        />

        {error && (
          <p role="alert" className="text-sm text-danger-500">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}

/** The attached account, shown on the cart so it is never charged to the wrong person unseen. */
export function AttachedCustomerRow({
  customer,
  onDetach,
  onAttach,
}: {
  customer: AttachedCustomer | null
  onDetach: () => void
  onAttach: () => void
}) {
  if (!customer) {
    return (
      <button
        type="button"
        onClick={onAttach}
        className="touch-target flex w-full items-center gap-2 border-b border-[var(--border-subtle)] px-4 text-sm text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
      >
        <UserRound className="h-4 w-4" />
        {t('Add a customer')}
      </button>
    )
  }

  return (
    <div className="flex items-center gap-2 border-b border-[var(--border-subtle)] bg-[var(--surface-sunken)] px-4 py-2.5">
      <UserRound className="h-4 w-4 shrink-0 text-[var(--accent)]" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{customer.name}</p>
        <p className="tabular truncate text-xs text-[var(--text-secondary)]">
          {formatUsd(Math.max(0, customer.availableCreditUsd))} of credit left
        </p>
      </div>
      <Button variant="ghost" size="icon" aria-label={t('Remove customer')} onClick={onDetach}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  )
}
